/**
 * MapControls Component
 * 
 * Provides map control buttons: search, refresh, fit all markers, current location.
 * Uses design tokens exclusively per Cursor Rules.
 */

import React, { useState, useCallback } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  StyleSheet, 
  Pressable, 
  ActivityIndicator,
  Keyboard,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, shadow } from '../../../shared/tokens/mobile';
import { logger } from '../../../shared/utils/logger';

interface MapControlsProps {
  mapboxToken: string | null;
  onRefresh: () => void;
  onFitAll: () => void;
  onFlyTo: (lat: number, lng: number, zoom?: number, showLocation?: boolean) => void;
  hasOpportunities: boolean;
  refreshing?: boolean;
}

interface GeocodingResult {
  id: string;
  place_name: string;
  center: [number, number]; // [lng, lat]
}

export function MapControls({
  mapboxToken,
  onRefresh,
  onFitAll,
  onFlyTo,
  hasOpportunities,
  refreshing,
}: MapControlsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<GeocodingResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [locating, setLocating] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || !mapboxToken) return;
    
    Keyboard.dismiss();
    setSearching(true);
    setShowResults(false);
    
    try {
      const encoded = encodeURIComponent(searchQuery.trim());
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${mapboxToken}&country=us&limit=5`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Geocoding failed');
      
      const data = await response.json();
      const results: GeocodingResult[] = (data.features || [])
        .filter((f: any) => f.center && Array.isArray(f.center) && f.center.length >= 2)
        .map((f: any) => ({
          id: f.id,
          place_name: f.place_name,
          center: f.center as [number, number],
        }));
      
      if (results.length === 0) {
        logger.info('No geocoding results', 'map');
      } else if (results.length === 1 && results[0].center) {
        // Single result - fly directly
        onFlyTo(results[0].center[1], results[0].center[0], 14);
        setSearchQuery('');
      } else {
        // Multiple results - show dropdown
        setSearchResults(results);
        setShowResults(true);
      }
    } catch (error) {
      logger.error('Geocoding error', 'map', String(error));
    } finally {
      setSearching(false);
    }
  }, [searchQuery, mapboxToken, onFlyTo]);

  const handleResultSelect = useCallback((result: GeocodingResult) => {
    if (result.center && result.center.length >= 2) {
      onFlyTo(result.center[1], result.center[0], 14);
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  }, [onFlyTo]);

  const handleCurrentLocation = useCallback(async () => {
    if (!navigator?.geolocation) {
      logger.warn('Geolocation not supported', 'map');
      return;
    }
    
    setLocating(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        onFlyTo(latitude, longitude, 14, true);
        setLocating(false);
      },
      (error) => {
        logger.warn('Geolocation error', 'map', error.message);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [onFlyTo]);

  return (
    <View style={styles.container}>
      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputWrapper}>
          <Ionicons name="search" size={20} color={color.textMuted} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search location..."
            placeholderTextColor={color.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={handleSearch}
            returnKeyType="search"
            autoCorrect={false}
          />
          {searching ? (
            <ActivityIndicator size="small" color={color.primary} style={styles.searchIndicator} />
          ) : searchQuery.length > 0 ? (
            <Pressable 
              onPress={() => {
                setSearchQuery('');
                setShowResults(false);
              }}
              style={styles.clearButton}
            >
              <Ionicons name="close-circle" size={20} color={color.textMuted} />
            </Pressable>
          ) : null}
        </View>
        
        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <View style={styles.resultsDropdown}>
            {searchResults.map((result) => (
              <Pressable
                key={result.id}
                style={({ pressed }) => [
                  styles.resultItem,
                  pressed && styles.resultItemPressed,
                ]}
                onPress={() => handleResultSelect(result)}
              >
                <Ionicons name="location-outline" size={16} color={color.textMuted} />
                <Text style={styles.resultText} numberOfLines={2}>{result.place_name}</Text>
              </Pressable>
            ))}
          </View>
        )}
      </View>
      
      {/* Control Buttons */}
      <View style={styles.buttonsRow}>
        <ControlButton
          icon="refresh"
          label="Refresh"
          onPress={onRefresh}
          loading={refreshing}
        />
        <ControlButton
          icon="contract-outline"
          label="Fit All"
          onPress={onFitAll}
          disabled={!hasOpportunities}
        />
        <ControlButton
          icon="locate"
          label="My Location"
          onPress={handleCurrentLocation}
          loading={locating}
        />
      </View>
    </View>
  );
}

interface ControlButtonProps {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function ControlButton({ icon, label, onPress, loading, disabled }: ControlButtonProps) {
  return (
    <Pressable
      style={({ pressed }) => [
        styles.controlButton,
        pressed && !disabled && styles.controlButtonPressed,
        disabled && styles.controlButtonDisabled,
      ]}
      onPress={onPress}
      disabled={disabled || loading}
    >
      {loading ? (
        <ActivityIndicator size="small" color={color.primary} />
      ) : (
        <Ionicons 
          name={icon} 
          size={18} 
          color={disabled ? color.disabled : color.primary} 
        />
      )}
      <Text style={[
        styles.controlButtonLabel,
        disabled && styles.controlButtonLabelDisabled,
      ]}>
        {label}
      </Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: space.sm,
    left: space.xl + space.xxl, // Account for basemap switcher
    right: space.xl + space.xxl, // Account for zoom controls
    gap: space.xs,
    zIndex: 10,
  },
  searchContainer: {
    position: 'relative',
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: color.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: color.border,
    ...shadow.sm,
  },
  searchIcon: {
    paddingLeft: space.sm,
  },
  searchInput: {
    flex: 1,
    height: 44,
    paddingHorizontal: space.sm,
    fontSize: text.body.fontSize,
    color: color.textPrimary,
  },
  searchIndicator: {
    paddingRight: space.sm,
  },
  clearButton: {
    padding: space.sm,
  },
  resultsDropdown: {
    position: 'absolute',
    top: 48,
    left: 0,
    right: 0,
    backgroundColor: color.surface,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: color.border,
    ...shadow.sm,
    maxHeight: 250,
    overflow: 'hidden',
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    padding: space.sm,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  resultItemPressed: {
    backgroundColor: color.backgroundSecondary,
  },
  resultText: {
    flex: 1,
    fontSize: text.caption.fontSize,
    color: color.textPrimary,
  },
  buttonsRow: {
    flexDirection: 'row',
    gap: space.xs,
    justifyContent: 'center',
  },
  controlButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    backgroundColor: color.surface,
    paddingVertical: space.xs,
    paddingHorizontal: space.sm,
    borderRadius: radius.full,
    ...shadow.sm,
  },
  controlButtonPressed: {
    backgroundColor: color.backgroundSecondary,
    transform: [{ scale: 0.98 }],
  },
  controlButtonDisabled: {
    opacity: 0.5,
  },
  controlButtonLabel: {
    fontSize: text.caption.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.primary,
  },
  controlButtonLabelDisabled: {
    color: color.disabled,
  },
});

